<template>
  <PopUp :show="modal" @hideModal="hideModal">
    <ValidationObserver
      v-slot="{ invalid }"
      tag="div"
      ref="observer"
      class="artist"
      id="scroll-block"
    >
      <header class="artist__header">
        <h3 class="artist__header_title">{{ artist.id ? "Edit" : "Add" }} Artist</h3>
        <div class="artist__header_actions">
          <BaseButton
            v-if="item"
            class="button--sm button--outline button--uppercase"
            text="Delete Artist"
            @click="$emit('removeArtist', artist)"
          ></BaseButton>
          <BaseButton
            class="button--sm button--green button--uppercase"
            text="Save Changes"
            :loading="changesLoading"
            :disabled="isArtistSaveDisabled"
            @click="saveArtist"
          ></BaseButton>
        </div>
      </header>
      <section class="form">
        <h3 class="form__header">Basic Information</h3>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ required: true, min: MIN_ON_TEXT_INPUT, max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="First Name"
              :required="true"
              :validateError="errors[0]"
              v-model="artist.first_name"
              :element="artist.first_name"
              @input="artist.first_name = $event"
            ></BaseInput>
          </ValidationProvider>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ required: true, min: MIN_ON_TEXT_INPUT, max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Last Name"
              :required="true"
              :validateError="errors[0]"
              v-model="artist.last_name"
              :element="artist.last_name"
              @input="artist.last_name = $event"
            ></BaseInput>
          </ValidationProvider>
        </div>
        <div class="form__row form__row--align-start">
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ required: true, path: true, max: MAX_ON_TEXT_INPUT }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Path"
              :required="true"
              :validateError="errors[0] || serverError.slug"
              v-model="artist.slug"
              :element="artist.slug"
              @input="artist.slug = $event"
            >
              <template #text>
                <span class="form-control__simple-text"
                  >Example: {{ `${url}/artists/${artist.slug || "path"}` }}</span
                ></template
              >
            </BaseInput>
          </ValidationProvider>
          <div class="form__row_col form__row_col--side">
            <Toggle
              placeholder="Status"
              v-model="artist.status"
              :disabled="invalid || artist.search_image === '' || artist.header_image === ''"
              :selected="+artist.status"
              :items="STATUS_LIST"
              @input="artist.status = $event"
            ></Toggle>
          </div>
        </div>
        <div class="form__row">
          <ValidationProvider tag="div" class="form__row_col" v-slot="{ errors }">
            <MultipleAutocompleteInput
              id="autocomplete-regions"
              :class="{ 'autocomplete-input--invalid': errors[0] }"
              v-model="artist.regions"
              :focusOpen="true"
              :validateError="errors[0]"
              :loading="regionsLoading"
              :items="artist.regions"
              :matches="regions"
              placeholder="Countries/Regions"
              @addItem="addRegion"
              @input="artist.regions = $event"
              @searchMatches="apiGetSearchedRegions"
            ></MultipleAutocompleteInput>
          </ValidationProvider>
        </div>
        <div class="form__row">
          <ValidationProvider tag="div" class="form__row_col">
            <BaseSelect
              placeholder="Classification"
              v-model="artist.is_secondary_market"
              :selected="artist.is_secondary_market"
              :items="[
                { id: '0', title: 'Our Artist' },
                { id: '1', title: 'Secondary Market' },
                { id: '2', title: 'Unlisted' },
              ]"
              :required="true"
              @input="artist.is_secondary_market = $event.id"
            ></BaseSelect>
          </ValidationProvider>
        </div>
      </section>
      <section class="form">
        <h3 class="form__header">Biography</h3>
        <ValidationProvider tag="div" class="form__row" :rules="{ required: true }">
          <Editor
            placeholder="Text Editor"
            :required="true"
            :value="artist.biography"
            @input="artist.biography = $event"
            v-model="artist.biography"
          ></Editor>
        </ValidationProvider>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ min: YEAR_LENGTH, max: YEAR_LENGTH }"
            v-slot="{ errors }"
          >
            <BaseNumberInput
              placeholder="Birth Year"
              :validateError="errors[0]"
              v-model="artist.birth_year"
              :element="artist.birth_year"
              @input="artist.birth_year = $event"
            >
            </BaseNumberInput>
          </ValidationProvider>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ min: YEAR_LENGTH, max: YEAR_LENGTH }"
            v-slot="{ errors }"
          >
            <BaseNumberInput
              placeholder="Death Year"
              :validateError="errors[0]"
              v-model="artist.death_year"
              :element="artist.death_year"
              @input="artist.death_year = $event"
            >
            </BaseNumberInput>
          </ValidationProvider>
        </div>
        <ValidationProvider
          tag="div"
          class="form__row_col"
          :rules="{ max: MAX_ON_TEXT_INPUT }"
          v-slot="{ errors }"
        >
          <BaseInput
            placeholder="Location"
            :validateError="errors[0]"
            v-model="artist.location"
            :element="artist.location"
            @input="artist.location = $event"
          >
          </BaseInput>
        </ValidationProvider>
      </section>
      <section class="form">
        <h3 class="form__header">Video</h3>
        <div
          class="form__row form__row--bordered form__row--justify-start"
          style="padding: 8px; align-items: flex-start"
        >
          <span class="form__row_label">Video </span>
          <div>
            <DragAndDropImage
              type="video"
              id="video"
              :accept="['mp4', 'mov', 'webm']"
              size="15"
              :image="artist.videos.url"
              @input="artist.videos.url = $event.image"
              @removeImage="
                artist.videos = {
                  url: '',
                  caption: '',
                }
              "
              style="margin: 4px"
            ></DragAndDropImage>
            <BaseInput
              v-if="artist.videos.url"
              class="form-control--xs"
              style="margin-top: 20px"
              placeholder="Caption"
              v-model="artist.videos.caption"
              :element="artist.videos.caption"
              @input="artist.videos.caption = $event"
            ></BaseInput>
          </div>
        </div>
      </section>
      <section class="form">
        <h3 class="form__header">CV highlights</h3>
        <div class="form__row">
          <Editor
            placeholder="Text Editor"
            :value="artist.past_shows_and_fair_booths"
            @input="artist.past_shows_and_fair_booths = $event"
            v-model="artist.past_shows_and_fair_booths"
          ></Editor>
        </div>
        <ValidationProvider
          tag="div"
          class="form__row_col"
          :rules="{ max: MAX_ON_TEXT_INPUT }"
          v-slot="{ errors }"
        >
          <BaseInput
            placeholder="View More Button URL"
            :validateError="errors[0]"
            v-model="artist.view_more_url"
            :element="artist.view_more_url"
            @input="artist.view_more_url = $event"
          >
          </BaseInput>
        </ValidationProvider>
      </section>
      <section class="form">
        <h3 class="form__header">Images</h3>
        <div class="form__row">
          <div class="form__row_col form__row_col--side form__row_col--bordered">
            <span class="form__row_label"
              >Search Image (recommended size: 1500×1296px)<span class="form__row_label_required"
                >*</span
              ></span
            >
            <DragAndDropImage
              class="uploader--full-width"
              id="search"
              size="15"
              :image="artist.search_image"
              :v-model="artist.search_image"
              @input="
                artist.search_image = $event.image;
                $event.removed ? removedImages.push($event.removed) : false;
              "
              @removeImage="
                removedImages.push($event);
                artist.search_image = '';
              "
            ></DragAndDropImage>
          </div>
          <div class="form__row_col form__row_col--side form__row_col--bordered">
            <span class="form__row_label"
              >Header Image (recommended size: 2560×780px)
              <span class="form__row_label_required">*</span></span
            >
            <DragAndDropImage
              class="uploader--full-width"
              id="header"
              size="15"
              :image="artist.header_image"
              :v-model="artist.header_image"
              @input="
                artist.header_image = $event.image;
                $event.removed ? removedImages.push($event.removed) : false;
              "
              @removeImage="
                removedImages.push($event);
                artist.header_image = '';
              "
            ></DragAndDropImage>
          </div>
        </div>
      </section>
      <section class="form" v-if="publications.length > 0">
        <h3 class="form__header">Publications</h3>
        <Table
          class="simple"
          :items="sortedItems"
          :headerItems="headerItems"
          :tableData="tableData"
          @clickAction="clickAction"
          @sortTable="sortTable"
        ></Table>
      </section>
    </ValidationObserver>
    <OrderModal
      v-if="orderModal"
      :modal="orderModal"
      :items="sortedItems"
      @reorder="reorderItems"
      @hideModal="orderModal = false"
    ></OrderModal>
  </PopUp>
</template>
<script>
import PopUp from "@/components/PopUp.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import BaseButton from "@/components/buttons/BaseButton.vue";
import BaseInput from "@/components/inputs/BaseInput.vue";
import BaseSelect from "@/components/inputs/BaseSelect.vue";
import BaseNumberInput from "@/components/inputs/BaseNumberInput.vue";
import DragAndDropImage from "@/components/DragAndDropImage.vue";
import Editor from "@/components/inputs/Editor.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import Toggle from "@/components/inputs/Toggle.vue";
import Table from "@/components/Table.vue";

import OrderModal from "@/views/admin/content/looseThreads/OrderModal.vue";
import MultipleAutocompleteInput from "@/components/inputs/MultipleAutocompleteInput.vue";
import { MIN_ON_TEXT_INPUT, MAX_ON_TEXT_INPUT, YEAR_LENGTH, STATUS_LIST } from "@/constants";

export default {
  props: { item: { type: Object, default: null }, modal: { type: Boolean, default: false } },
  components: {
    PopUp,
    ValidationObserver,
    ValidationProvider,
    BaseButton,
    BaseInput,
    BaseSelect,
    BaseNumberInput,
    DragAndDropImage,
    Editor,
    Toggle,
    Table,
    OrderModal,
    MultipleAutocompleteInput,
  },
  data() {
    return {
      url: process.env.VUE_APP_FRONTEND_URL,
      artist: {
        id: "",
        first_name: "",
        last_name: "",
        slug: "",
        status: "0",
        biography: "",
        birth_year: "",
        death_year: "",
        location: "",
        regions: [],
        search_image: "",
        header_image: "",
        past_shows_and_fair_booths: "",
        view_more_url: "",
        is_secondary_market: "0",
        videos: {
          url: "",
          caption: "",
        },
      },
      tableData: {
        sort: "title",
        order: "asc",
      },
      removedImages: [],
      changed: false,
      orderModal: false,
      orderType: null,
      MIN_ON_TEXT_INPUT,
      MAX_ON_TEXT_INPUT,
      YEAR_LENGTH,
      STATUS_LIST,
    };
  },
  async created() {
    if (this.item != null) {
      this.artist = {
        ...JSON.parse(JSON.stringify(this.item)),
        videos: this.item.videos ? this.item.videos : { url: "", caption: "" },
      };
      await this.apiGetPublications({ id: this.item.id, ...this.tableData });
    }
  },
  beforeDestroy() {
    this.setError(null);
  },
  computed: {
    ...mapGetters("general", {
      regions: "getSearchedRegions",
      regionsLoading: "getChangesRegionsLoading",
    }),
    ...mapGetters("artists", {
      serverError: "getServerError",
      changesLoading: "getChangesLoading",
      headerItems: "getPublicationsHeaders",
      publications: "getPublications",
    }),
    sortedItems() {
      return this.publications.sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));
    },
    isArtistSaveDisabled() {
      return (
        this.invalid ||
        this.artist.search_image === "" ||
        this.artist.header_image === "" ||
        !this.changed
      );
    },
  },
  watch: {
    artist: {
      handler: function (val, oldVal) {
        if (this.item != null) {
          if (this.$refs.observer.flags.dirty) {
            this.changed = true;
          }
        } else {
          this.changed = true;
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions("general", ["apiGetSearchedRegions", "apiPostRegion"]),
    ...mapActions("artists", ["apiGetPublications", "apiPutChoseOrder"]),
    ...mapMutations("artists", ["setError"]),

    async sortTable({ sort, order }) {
      let data = { id: this.artist.id, ...this.tableData, page: 1, sort, order };
      let success = await this.apiGetPublications(data);
      if (success) {
        this.tableData = data;
      }
    },
    async clickAction({ item }) {
      this.orderType = item.orderType;
      this.orderModal = true;
    },
    async reorderItems(items) {
      let data = { ...this.tableData };
      let result = await this.apiPutChoseOrder({ id: this.artist.id, publications: items });
      if (result) {
        let success = await this.apiGetPublications({ id: this.artist.id, ...data });
        if (success) {
          this.tableData = data;
        }
        this.orderModal = false;
        this.orderType = null;
      }
    },
    async addRegion(val) {
      let data = { title: val };
      let result = await this.apiPostRegion(data);
      if (result.success) {
        this.art.regions.push(result.item);
      } else {
        this.$toasted.show(result.error, {
          duration: 2000,
          type: "error",
        });
      }
    },
    hideModal() {
      if (this.changed) {
        this.$swal({
          html: "<h2>Are you sure you want to exit <br/> without saving?</h2>",
          showCancelButton: true,
          cancelButtonText: "Exit without saving",
          confirmButtonText: "Save changes",
        }).then(result => {
          if (result.value) {
            this.$refs.observer.flags.valid &&
            artist.search_image != "" &&
            artist.header_image != ""
              ? this.saveArtist()
              : this.showToast();
          } else this.$emit("hideModal");
        });
      } else this.$emit("hideModal");
    },
    showToast() {
      this.$toasted.show(`Please fill in all required fields`, {
        duration: 2000,
        type: "error",
      });
    },
    saveArtist() {
      let data = {
        ...this.artist,
        regions: this.artist.regions
          ? this.artist.regions.map(el => {
              return el.id.toString();
            })
          : [],
      };
      if (this.artist.id) {
        this.$emit("updateArtist", { data: data, imageForRemove: this.removedImages });
      } else {
        this.$emit("createArtist", { data: data, imageForRemove: this.removedImages });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.artist {
  @include pageHeaderWrapper;
}
</style>

